import { usePostHog } from '@posthog/react';
import { useCallback, useMemo } from 'react';
import { useIsFeatureEnabled } from '../../api/features/features';
import { useGetClaimsForCurrentUser } from '../../api/user/user';
import { appInsights } from '../../ApplicationInsightsService';
import { PostHogEnabled } from '../../utils/FeatureFlags.constants';
import { getUserRedactionTermsFromClaims, redactTermsInRecord, redactTermsInText } from '../redaction';

/**
 * Hook that returns a trackEvent function for custom analytics.
 * For most cases this is a fire-and-forget function. When you need to know analytics can send before doing something else (for example clearing UTM query params), check isReady before calling trackEvent.
 * Use this hook mainly to track user actions.
 *
 * isReady is true when Application Insights is available, the PostHog feature-flag query has finished loading, and (if the flag is true) PostHog is initialized. It is false while the feature flag is loading, when App Insights is missing, or when the flag is true but PostHog is not yet available.
 *
 * trackEvent does not return a value. Application Insights is redacted here (no before_send). PostHog custom and autocapture events are redacted in `sanitizePostHogEvent` using claims from `initializePostHog`.
 * @example
 * const { trackEvent, isReady } = useEventTracker();
 * trackEvent(ACTION_INVOKED_EVENT, { action_name: PRINT_REPORT_MODAL_CLOSED, entry_point: ENTRY_POINT_DIALOG_CLOSE });
 */
export function useEventTracker() {
    const posthog = usePostHog();
    const { data: claims } = useGetClaimsForCurrentUser();
    const redactionTerms = useMemo(() => getUserRedactionTermsFromClaims(claims), [claims]);
    const { data: isPostHogFeatureEnabled, isLoading: isPostHogFeatureFlagLoading } = useIsFeatureEnabled({
        featureFlagName: PostHogEnabled,
    });
    const trackEvent = useCallback(
        (eventName: string, properties?: Record<string, unknown>): void => {
            try {
                // App Insights has no before_send — redact here.
                const sanitizedEventName = redactTermsInText(eventName, redactionTerms);
                const sanitizedProperties = properties ? redactTermsInRecord(properties, redactionTerms) : properties;
                appInsights?.trackEvent({ name: sanitizedEventName }, sanitizedProperties);

                // PostHog: sanitizePostHogEvent redacts in before_send using claims from initializePostHog.
                if (isPostHogFeatureEnabled) {
                    posthog?.capture(eventName, properties);
                }
            } catch (error) {
                console.error('[Analytics] trackEvent failed:', error);
            }
        },
        [posthog, isPostHogFeatureEnabled, redactionTerms]
    );

    const isReady = Boolean(appInsights && !isPostHogFeatureFlagLoading && (isPostHogFeatureEnabled !== true || posthog !== undefined));

    return { trackEvent, isReady };
}
